/**
 * Mock Data Service Module
 *
 * Provides mock data for demo mode, including realistic SORA evidence data
 * for all four review pages (Ground Risk Answers, Ground Risk Mitigations,
 * Air Risk Answers, Air Risk Mitigations).
 *
 * Also provides mock data for ContentProcessorAPI integration including:
 * - Mock schemas for document processing
 * - Simulated processing delays
 * - Mock responses matching ContentProcessorAPI format
 *
 * Requirements: 10.1, 10.2, 10.3, 12.2, 12.3, 12.4, 12.5
 */

import type {
  Document,
  UploadedDocument,
  ExtractionResults,
  ProcessingJob,
  Citation,
  GroundRiskAnswer,
  GroundRiskMitigation,
  AirRiskAnswer,
  AirRiskMitigation,
  ConfidenceLevel,
} from '../types';

import type {
  Schema,
  ProcessedDocument,
  ProcessStepOutput,
  ExtractionComparisonItem,
} from '../types/documents';

import type {
  SubmitResponse,
} from '../types/api';

import type { ProcessingStatus } from '../types/extraction';

// ============================================================================
// Mock Citations
// ============================================================================

const mockCitations: Record<string, Citation> = {
  conops_dimensions: {
    id: 'cit-001',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 12,
    sectionReference: 'Section 3.2 - Aircraft Specifications',
    excerpt: 'The DJI Matrice 300 RTK unmanned aircraft has maximum dimensions of 810mm × 670mm × 430mm (L×W×H) with propellers unfolded. The diagonal wheelbase measures 895mm.',
    highlightedText: '810mm × 670mm × 430mm',
  },
  conops_speed: {
    id: 'cit-002',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 14,
    sectionReference: 'Section 3.4 - Performance Characteristics',
    excerpt: 'Maximum horizontal flight speed is 23 m/s (82.8 km/h) in P-mode. During survey operations, typical cruise speed is maintained at 12-15 m/s for optimal data capture.',
    highlightedText: '23 m/s',
  },
  ops_manual_weight: {
    id: 'cit-003',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 8,
    sectionReference: 'Section 2.1 - Aircraft Details',
    excerpt: 'Maximum take-off weight (MTOW) of the DJI Matrice 300 RTK is 9kg including payload. Standard survey configuration with Zenmuse P1 camera weighs approximately 7.2kg.',
    highlightedText: '9kg including payload',
  },
  conops_controlled_area: {
    id: 'cit-004',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 22,
    sectionReference: 'Section 5.1 - Operational Area',
    excerpt: 'Operations will be conducted within a controlled ground area established by the operator. Ground personnel will maintain visual contact and control access to the operational volume.',
    highlightedText: 'controlled ground area',
  },
  conops_population: {
    id: 'cit-005',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 25,
    sectionReference: 'Section 5.3 - Population Density Assessment',
    excerpt: 'The operational area is classified as sparsely populated with an estimated population density of less than 50 people per square kilometer. The ground risk buffer extends 50m beyond the operational volume.',
    highlightedText: 'less than 50 people per square kilometer',
  },
  ops_manual_sheltering: {
    id: 'cit-006',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 45,
    sectionReference: 'Section 8.2 - Ground Risk Mitigations',
    excerpt: 'Natural sheltering is available in the form of tree coverage and terrain features. However, no purpose-built sheltering structures are present in the typical operational environment.',
    highlightedText: 'Natural sheltering is available',
  },
  conops_restrictions: {
    id: 'cit-007',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 28,
    sectionReference: 'Section 5.5 - Operational Restrictions',
    excerpt: 'Flight operations are restricted to daylight hours only, with minimum visibility of 5km. Operations will not be conducted over gatherings of people or within 50m of uninvolved persons.',
    highlightedText: 'restricted to daylight hours only',
  },
  ops_manual_observers: {
    id: 'cit-008',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 52,
    sectionReference: 'Section 9.1 - Ground Observer Procedures',
    excerpt: 'Ground observers are deployed at strategic positions to monitor the operational area. Each observer maintains radio communication with the remote pilot and can initiate emergency procedures.',
    highlightedText: 'Ground observers are deployed',
  },
  conops_impact: {
    id: 'cit-009',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 32,
    sectionReference: 'Section 6.2 - Impact Mitigation',
    excerpt: 'The aircraft is equipped with a parachute recovery system (DJI FlyCart 30 compatible) that deploys automatically upon detection of critical failure. Terminal velocity with parachute deployed is reduced to 5 m/s.',
    highlightedText: 'parachute recovery system',
  },
  conops_aae: {
    id: 'cit-010',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 35,
    sectionReference: 'Section 7.1 - Airspace Assessment',
    excerpt: 'The operational area is not within an Atypical Air Environment. Standard airspace rules apply. The area is outside controlled airspace and below the transition altitude.',
    highlightedText: 'not within an Atypical Air Environment',
  },
  ops_manual_ifp: {
    id: 'cit-011',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 62,
    sectionReference: 'Section 10.2 - Airspace Considerations',
    excerpt: 'Pre-flight planning includes verification of instrument flight procedures in the area. The typical operational area is outside published IFP routes but within 5nm of an aerodrome.',
    highlightedText: 'outside published IFP routes',
  },
  conops_vfr: {
    id: 'cit-012',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 38,
    sectionReference: 'Section 7.3 - VFR Traffic',
    excerpt: 'The operational area intersects with a known VFR corridor used by light aircraft and helicopters. Enhanced vigilance and additional observers are deployed when operating in this area.',
    highlightedText: 'known VFR corridor',
  },
  ops_manual_altitude: {
    id: 'cit-013',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 65,
    sectionReference: 'Section 10.4 - Altitude Restrictions',
    excerpt: 'Maximum operational altitude is 400ft AGL in accordance with standard UAS regulations. Survey operations typically conducted between 100-300ft AGL for optimal data quality.',
    highlightedText: '400ft AGL',
  },
  conops_operation_type: {
    id: 'cit-014',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 18,
    sectionReference: 'Section 4.1 - Operation Category',
    excerpt: 'This operation is conducted as Extended Visual Line of Sight (EVLOS) with visual observers maintaining sight of the aircraft at all times. The remote pilot may lose direct visual contact but observers provide continuous visual coverage.',
    highlightedText: 'Extended Visual Line of Sight (EVLOS)',
  },
  // Air Risk Mitigation Citations
  sm1_boundary: {
    id: 'cit-015',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 42,
    sectionReference: 'Section 8.1 - Strategic Mitigations',
    excerpt: 'Operational boundaries are clearly defined using GPS geofencing. The aircraft will automatically return to home if boundary limits are approached. A 50m buffer zone is maintained from all boundary edges.',
    highlightedText: 'GPS geofencing',
  },
  sm2_chronology: {
    id: 'cit-016',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 72,
    sectionReference: 'Section 11.2 - Temporal Restrictions',
    excerpt: 'Operations are scheduled to avoid peak air traffic periods. Flight windows are coordinated with local ATC where applicable. No operations during official sunrise/sunset periods.',
    highlightedText: 'avoid peak air traffic periods',
  },
  sm3_exposure: {
    id: 'cit-017',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 44,
    sectionReference: 'Section 8.3 - Exposure Time',
    excerpt: 'Individual flight duration is limited to 30 minutes maximum. Total daily exposure time in any single airspace volume is limited to 4 hours. Rest periods of minimum 15 minutes between flights.',
    highlightedText: '30 minutes maximum',
  },
  sm4_sua: {
    id: 'cit-018',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 75,
    sectionReference: 'Section 11.4 - Special Use Airspace',
    excerpt: 'Operations do not typically occur within Special Use Airspace. When SUA operations are required, specific authorization is obtained from the controlling authority prior to flight.',
    highlightedText: 'do not typically occur within Special Use Airspace',
  },
  sm5_airspace: {
    id: 'cit-019',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 46,
    sectionReference: 'Section 8.5 - Airspace Requirements',
    excerpt: 'All applicable airspace requirements are identified during pre-flight planning. NOTAMs are checked within 24 hours of operation. Temporary airspace restrictions are monitored continuously.',
    highlightedText: 'NOTAMs are checked within 24 hours',
  },
  sm6_ansp: {
    id: 'cit-020',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 78,
    sectionReference: 'Section 11.6 - ANSP Coordination',
    excerpt: 'For operations near controlled airspace, coordination with the Air Navigation Service Provider is established prior to flight. Standard phraseology and communication procedures are documented.',
    highlightedText: 'coordination with the Air Navigation Service Provider',
  },
  sm7_utm: {
    id: 'cit-021',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 48,
    sectionReference: 'Section 8.7 - UTM Integration',
    excerpt: 'The operation is registered with the UTM service provider where available. Real-time position data is shared through the UTM network. Conflict alerts are monitored throughout the flight.',
    highlightedText: 'registered with the UTM service provider',
  },
  sm8_notam: {
    id: 'cit-022',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 80,
    sectionReference: 'Section 11.8 - NOTAM Procedures',
    excerpt: 'A NOTAM is filed for operations exceeding 400ft AGL or within 5nm of an aerodrome. Standard NOTAM format is used with accurate position, altitude, and timing information.',
    highlightedText: 'NOTAM is filed',
  },
  sm9_military: {
    id: 'cit-023',
    documentId: 'doc-001',
    documentName: 'Concept of Operations - Survey Drone v2.1.pdf',
    pageNumber: 50,
    sectionReference: 'Section 8.9 - Military Coordination',
    excerpt: 'Operations in areas with known military low flying activity are notified to the relevant military authority. The Low Flying Booking Cell is contacted for operations in designated areas.',
    highlightedText: 'notified to the relevant military authority',
  },
  sm10_outreach: {
    id: 'cit-024',
    documentId: 'doc-002',
    documentName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    pageNumber: 82,
    sectionReference: 'Section 11.10 - Local Outreach',
    excerpt: 'Local flying clubs and known pilots in the area are informed of planned operations through direct contact and notice boards at nearby airfields. Contact details are exchanged for real-time coordination.',
    highlightedText: 'Local flying clubs and known pilots',
  },
};

// ============================================================================
// Mock Schemas (ContentProcessorAPI format)
// Requirements: 10.1, 10.3
// ============================================================================

const mockSchemas: Schema[] = [
  {
    id: 'schema-001',
    className: 'SORA_Evidence',
    description: 'Schema for extracting SORA (Specific Operations Risk Assessment) evidence from drone operation documents. Extracts ground risk answers, ground risk mitigations, air risk answers, and air risk mitigations.',
    fileName: 'sora_evidence_schema.json',
    contentType: 'application/json',
    createdOn: '2024-01-01T00:00:00Z',
    updatedOn: '2024-03-01T00:00:00Z',
  },
  {
    id: 'schema-002',
    className: 'ConOps_Summary',
    description: 'Schema for extracting key information from Concept of Operations documents including aircraft specifications, operational parameters, and safety measures.',
    fileName: 'conops_summary_schema.json',
    contentType: 'application/json',
    createdOn: '2024-01-15T00:00:00Z',
    updatedOn: '2024-02-15T00:00:00Z',
  },
  {
    id: 'schema-003',
    className: 'Operations_Manual',
    description: 'Schema for extracting operational procedures, safety protocols, and compliance information from Operations Manual documents.',
    fileName: 'operations_manual_schema.json',
    contentType: 'application/json',
    createdOn: '2024-02-01T00:00:00Z',
    updatedOn: '2024-03-10T00:00:00Z',
  },
  {
    id: 'schema-004',
    className: 'Risk_Assessment',
    description: 'Schema for extracting risk assessment data including hazard identification, risk evaluation, and mitigation strategies.',
    fileName: 'risk_assessment_schema.json',
    contentType: 'application/json',
    createdOn: '2024-02-20T00:00:00Z',
    updatedOn: null,
  },
];

// ============================================================================
// Mock ProcessedDocuments (ContentProcessorAPI format)
// Requirements: 10.1, 10.3
// ============================================================================

const mockProcessStepOutputs: ProcessStepOutput[] = [
  {
    stepName: 'document_ingestion',
    processedTime: '2024-03-15T14:25:30Z',
    stepResult: { pages_processed: 45, text_extracted: true },
  },
  {
    stepName: 'schema_matching',
    processedTime: '2024-03-15T14:26:00Z',
    stepResult: { schema_id: 'schema-001', match_score: 0.92 },
  },
  {
    stepName: 'entity_extraction',
    processedTime: '2024-03-15T14:28:00Z',
    stepResult: { entities_found: 24, confidence_avg: 0.85 },
  },
  {
    stepName: 'validation',
    processedTime: '2024-03-15T14:29:00Z',
    stepResult: { validation_passed: true, warnings: [] },
  },
];

const mockExtractionComparisonData: ExtractionComparisonItem[] = [
  {
    field: 'max_dimensions',
    extracted: '810mm × 670mm × 430mm',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'max_speed_mps',
    extracted: '23 m/s',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'weight_over_250g',
    extracted: 'Yes (MTOW: 9kg)',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'controlled_ground_area_only',
    extracted: 'Yes',
    confidence: 'Medium',
    isAboveThreshold: true,
  },
  {
    field: 'max_population_density',
    extracted: 'Less than 50 people per km²',
    confidence: 'Low',
    isAboveThreshold: false,
  },
  {
    field: 'M1A',
    extracted: 'low',
    confidence: 'Medium',
    isAboveThreshold: true,
  },
  {
    field: 'M1B',
    extracted: 'medium',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'M1C',
    extracted: 'low',
    confidence: 'Medium',
    isAboveThreshold: true,
  },
  {
    field: 'M2',
    extracted: 'high',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'aae_only',
    extracted: 'false',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'known_ifp_area',
    extracted: 'false',
    confidence: 'Medium',
    isAboveThreshold: true,
  },
  {
    field: 'vfr_corridor_area',
    extracted: 'true',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'above_500ft_agl',
    extracted: 'false',
    confidence: 'High',
    isAboveThreshold: true,
  },
  {
    field: 'operation_type',
    extracted: 'BVLOS_visual_mitigations',
    confidence: 'Low',
    isAboveThreshold: false,
  },
];

const mockProcessedDocuments: ProcessedDocument[] = [
  {
    processId: 'proc-001',
    processedFileName: 'Concept of Operations - Survey Drone v2.1.pdf',
    processedFileMimeType: 'application/pdf',
    processedTime: '2024-03-15T14:30:00Z',
    importedTime: '2024-03-15T14:25:00Z',
    lastModifiedTime: '2024-03-15T14:30:00Z',
    lastModifiedBy: 'system',
    status: 'completed',
    entityScore: 0.87,
    minExtractedEntityScore: 0.6,
    schemaScore: 0.92,
    result: {
      summary: 'Successfully extracted SORA evidence from uploaded documents',
      ground_risk_answers: 5,
      ground_risk_mitigations: 4,
      air_risk_answers: 5,
      air_risk_mitigations: 10,
    },
    confidence: {
      overall: 0.85,
      ground_risk: 0.88,
      air_risk: 0.82,
    },
    targetSchema: mockSchemas[0],
    promptTokens: 4523,
    completionTokens: 2187,
    processOutput: mockProcessStepOutputs,
    extractedComparisonData: mockExtractionComparisonData,
    comment: null,
  },
  {
    processId: 'proc-002',
    processedFileName: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    processedFileMimeType: 'application/pdf',
    processedTime: '2024-03-16T10:15:00Z',
    importedTime: '2024-03-16T10:10:00Z',
    lastModifiedTime: '2024-03-16T10:15:00Z',
    lastModifiedBy: 'user-001',
    status: 'completed',
    entityScore: 0.91,
    minExtractedEntityScore: 0.6,
    schemaScore: 0.95,
    result: {
      summary: 'Extracted operational procedures and safety protocols',
      procedures_found: 12,
      safety_protocols: 8,
    },
    confidence: {
      overall: 0.89,
      procedures: 0.92,
      safety: 0.86,
    },
    targetSchema: mockSchemas[2],
    promptTokens: 5234,
    completionTokens: 2456,
    processOutput: [
      {
        stepName: 'document_ingestion',
        processedTime: '2024-03-16T10:10:30Z',
        stepResult: { pages_processed: 82, text_extracted: true },
      },
      {
        stepName: 'schema_matching',
        processedTime: '2024-03-16T10:11:00Z',
        stepResult: { schema_id: 'schema-003', match_score: 0.95 },
      },
      {
        stepName: 'entity_extraction',
        processedTime: '2024-03-16T10:14:00Z',
        stepResult: { entities_found: 20, confidence_avg: 0.89 },
      },
      {
        stepName: 'validation',
        processedTime: '2024-03-16T10:15:00Z',
        stepResult: { validation_passed: true, warnings: [] },
      },
    ],
    extractedComparisonData: [
      {
        field: 'emergency_procedures',
        extracted: 'Documented in Section 8',
        confidence: 'High',
        isAboveThreshold: true,
      },
      {
        field: 'maintenance_schedule',
        extracted: 'Monthly inspections required',
        confidence: 'High',
        isAboveThreshold: true,
      },
    ],
    comment: 'Reviewed and approved by operations manager',
  },
  {
    processId: 'proc-003',
    processedFileName: 'Risk Assessment - Agricultural Survey.pdf',
    processedFileMimeType: 'application/pdf',
    processedTime: null,
    importedTime: '2024-03-17T09:00:00Z',
    lastModifiedTime: '2024-03-17T09:00:00Z',
    lastModifiedBy: null,
    status: 'processing',
    entityScore: 0,
    minExtractedEntityScore: 0.6,
    schemaScore: 0,
    result: null,
    confidence: null,
    targetSchema: mockSchemas[3],
    promptTokens: 0,
    completionTokens: 0,
    processOutput: [
      {
        stepName: 'document_ingestion',
        processedTime: '2024-03-17T09:00:30Z',
        stepResult: { pages_processed: 15, text_extracted: true },
      },
    ],
    extractedComparisonData: null,
    comment: null,
  },
];

// ============================================================================
// Mock Documents
// ============================================================================

const mockDocuments: Document[] = [
  {
    id: 'doc-001',
    name: 'Concept of Operations - Survey Drone v2.1.pdf',
    classification: 'concept_of_operations',
    mimeType: 'application/pdf',
    size: 2456789,
    blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-001/v2/conops.pdf',
    currentVersion: 2,
    versions: [
      {
        version: 1,
        blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-001/v1/conops.pdf',
        size: 2234567,
        uploadedAt: '2024-01-15T10:30:00Z',
        uploadedBy: 'user-001',
      },
      {
        version: 2,
        blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-001/v2/conops.pdf',
        size: 2456789,
        uploadedAt: '2024-02-20T14:45:00Z',
        uploadedBy: 'user-001',
      },
    ],
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-02-20T14:45:00Z',
    createdBy: 'user-001',
  },
  {
    id: 'doc-002',
    name: 'Operations Manual - Aerial Survey Ltd v3.0.pdf',
    classification: 'operational_manual',
    mimeType: 'application/pdf',
    size: 5678901,
    blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-002/v3/ops-manual.pdf',
    currentVersion: 3,
    versions: [
      {
        version: 1,
        blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-002/v1/ops-manual.pdf',
        size: 4567890,
        uploadedAt: '2023-11-01T09:00:00Z',
        uploadedBy: 'user-001',
      },
      {
        version: 2,
        blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-002/v2/ops-manual.pdf',
        size: 5123456,
        uploadedAt: '2024-01-10T11:20:00Z',
        uploadedBy: 'user-001',
      },
      {
        version: 3,
        blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-002/v3/ops-manual.pdf',
        size: 5678901,
        uploadedAt: '2024-03-05T16:30:00Z',
        uploadedBy: 'user-002',
      },
    ],
    createdAt: '2023-11-01T09:00:00Z',
    updatedAt: '2024-03-05T16:30:00Z',
    createdBy: 'user-001',
  },
  {
    id: 'doc-003',
    name: 'Risk Assessment - Agricultural Survey Project.pdf',
    classification: 'other',
    mimeType: 'application/pdf',
    size: 1234567,
    blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-003/v1/risk-assessment.pdf',
    currentVersion: 1,
    versions: [
      {
        version: 1,
        blobUrl: 'https://storage.blob.core.windows.net/evidence-documents/doc-003/v1/risk-assessment.pdf',
        size: 1234567,
        uploadedAt: '2024-03-10T08:15:00Z',
        uploadedBy: 'user-001',
      },
    ],
    createdAt: '2024-03-10T08:15:00Z',
    updatedAt: '2024-03-10T08:15:00Z',
    createdBy: 'user-001',
  },
];

// ============================================================================
// Mock Ground Risk Answers (5 fields with varied confidence levels)
// ============================================================================

const mockGroundRiskAnswers: GroundRiskAnswer[] = [
  {
    id: 'gra-001',
    field: 'max_dimensions',
    extractedValue: '810mm × 670mm × 430mm (diagonal wheelbase: 895mm)',
    confidence: 'high' as ConfidenceLevel,
    citations: [mockCitations.conops_dimensions],
  },
  {
    id: 'gra-002',
    field: 'max_speed_mps',
    extractedValue: '23 m/s',
    confidence: 'high' as ConfidenceLevel,
    citations: [mockCitations.conops_speed],
  },
  {
    id: 'gra-003',
    field: 'weight_over_250g',
    extractedValue: 'Yes (MTOW: 9kg)',
    confidence: 'high' as ConfidenceLevel,
    citations: [mockCitations.ops_manual_weight],
  },
  {
    id: 'gra-004',
    field: 'controlled_ground_area_only',
    extractedValue: 'Yes',
    confidence: 'medium' as ConfidenceLevel,
    citations: [mockCitations.conops_controlled_area],
  },
  {
    id: 'gra-005',
    field: 'max_population_density',
    extractedValue: 'Less than 50 people per km² (sparsely populated)',
    confidence: 'low' as ConfidenceLevel,
    citations: [mockCitations.conops_population],
  },
];

// ============================================================================
// Mock Ground Risk Mitigations (M1A, M1B, M1C, M2)
// ============================================================================

const mockGroundRiskMitigations: GroundRiskMitigation[] = [
  {
    id: 'grm-001',
    code: 'M1A',
    name: 'Sheltering',
    selectedLevel: 'low',
    commentary: 'The operational area provides natural sheltering through tree coverage and terrain features. While no purpose-built sheltering structures are present, the natural environment offers some protection to uninvolved persons. The assessment concludes that a LOW level of sheltering mitigation is applicable based on the available natural cover in the typical survey environment.',
    evidence: 'Natural sheltering is available in the form of tree coverage and terrain features. However, no purpose-built sheltering structures are present in the typical operational environment.',
    citations: [mockCitations.ops_manual_sheltering],
  },
  {
    id: 'grm-002',
    code: 'M1B',
    name: 'Operational Restrictions',
    selectedLevel: 'medium',
    commentary: 'Comprehensive operational restrictions are in place including daylight-only operations, minimum 5km visibility requirements, and prohibition of flight over gatherings or within 50m of uninvolved persons. These restrictions significantly reduce ground risk exposure. The MEDIUM level is assessed based on the combination of temporal, meteorological, and spatial restrictions documented in the ConOps.',
    evidence: 'Flight operations are restricted to daylight hours only, with minimum visibility of 5km. Operations will not be conducted over gatherings of people or within 50m of uninvolved persons.',
    citations: [mockCitations.conops_restrictions],
  },
  {
    id: 'grm-003',
    code: 'M1C',
    name: 'Ground Observations',
    selectedLevel: 'low',
    commentary: 'Ground observers are deployed at strategic positions throughout the operational area. Each observer maintains continuous radio communication with the remote pilot and has authority to initiate emergency procedures. The LOW level is assessed as observers provide situational awareness but cannot physically prevent ground impact.',
    evidence: 'Ground observers are deployed at strategic positions to monitor the operational area. Each observer maintains radio communication with the remote pilot and can initiate emergency procedures.',
    citations: [mockCitations.ops_manual_observers],
  },
  {
    id: 'grm-004',
    code: 'M2',
    name: 'Reducing impact dynamics',
    selectedLevel: 'high',
    commentary: 'The aircraft is equipped with an automatic parachute recovery system that significantly reduces terminal velocity to 5 m/s upon deployment. This system activates automatically upon detection of critical failure conditions. The HIGH level mitigation is assessed based on the proven effectiveness of the parachute system in reducing impact energy and the automatic deployment capability.',
    evidence: 'The aircraft is equipped with a parachute recovery system that deploys automatically upon detection of critical failure. Terminal velocity with parachute deployed is reduced to 5 m/s.',
    citations: [mockCitations.conops_impact],
  },
];

// ============================================================================
// Mock Air Risk Answers (5 fields with varied confidence levels)
// ============================================================================

const mockAirRiskAnswers: AirRiskAnswer[] = [
  {
    id: 'ara-001',
    field: 'aae_only',
    extractedValue: false,
    confidence: 'high' as ConfidenceLevel,
    citations: [mockCitations.conops_aae],
  },
  {
    id: 'ara-002',
    field: 'known_ifp_area',
    extractedValue: false,
    confidence: 'medium' as ConfidenceLevel,
    citations: [mockCitations.ops_manual_ifp],
  },
  {
    id: 'ara-003',
    field: 'vfr_corridor_area',
    extractedValue: true,
    confidence: 'high' as ConfidenceLevel,
    citations: [mockCitations.conops_vfr],
  },
  {
    id: 'ara-004',
    field: 'above_500ft_agl',
    extractedValue: false,
    confidence: 'high' as ConfidenceLevel,
    citations: [mockCitations.ops_manual_altitude],
  },
  {
    id: 'ara-005',
    field: 'operation_type',
    extractedValue: 'BVLOS_visual_mitigations',
    confidence: 'low' as ConfidenceLevel,
    citations: [mockCitations.conops_operation_type],
  },
];

// ============================================================================
// Mock Air Risk Mitigations (SM1-SM10)
// ============================================================================

const mockAirRiskMitigations: AirRiskMitigation[] = [
  {
    id: 'arm-001',
    code: 'SM1',
    name: 'Operational restriction by boundary',
    selectedValue: true,
    commentary: 'GPS geofencing is implemented to enforce operational boundaries. The aircraft will automatically initiate return-to-home procedures if boundary limits are approached. A 50m buffer zone is maintained from all boundary edges to provide additional safety margin. This strategic mitigation effectively contains the operation within defined airspace volumes.',
    evidence: 'Operational boundaries are clearly defined using GPS geofencing. The aircraft will automatically return to home if boundary limits are approached. A 50m buffer zone is maintained from all boundary edges.',
    citations: [mockCitations.sm1_boundary],
  },
  {
    id: 'arm-002',
    code: 'SM2',
    name: 'Operational restriction by chronology',
    selectedValue: true,
    commentary: 'Flight operations are scheduled to avoid peak air traffic periods in the operational area. Coordination with local ATC is established where applicable. Operations are prohibited during official sunrise and sunset periods when visibility conditions may be compromised. This temporal deconfliction reduces encounter probability with manned aircraft.',
    evidence: 'Operations are scheduled to avoid peak air traffic periods. Flight windows are coordinated with local ATC where applicable. No operations during official sunrise/sunset periods.',
    citations: [mockCitations.sm2_chronology],
  },
  {
    id: 'arm-003',
    code: 'SM3',
    name: 'Operational restriction by time of exposure',
    selectedValue: true,
    commentary: 'Individual flight duration is strictly limited to 30 minutes maximum to minimize airspace exposure time. Total daily exposure in any single airspace volume is capped at 4 hours. Mandatory rest periods of minimum 15 minutes between flights ensure crew alertness and reduce cumulative risk exposure.',
    evidence: 'Individual flight duration is limited to 30 minutes maximum. Total daily exposure time in any single airspace volume is limited to 4 hours. Rest periods of minimum 15 minutes between flights.',
    citations: [mockCitations.sm3_exposure],
  },
  {
    id: 'arm-004',
    code: 'SM4',
    name: 'Special Use Airspace (SUA)',
    selectedValue: false,
    commentary: 'Standard operations do not occur within Special Use Airspace. The typical operational areas are outside designated SUA boundaries. When exceptional circumstances require SUA operations, specific authorization is obtained from the controlling authority prior to any flight activity.',
    evidence: 'Operations do not typically occur within Special Use Airspace. When SUA operations are required, specific authorization is obtained from the controlling authority prior to flight.',
    citations: [mockCitations.sm4_sua],
  },
  {
    id: 'arm-005',
    code: 'SM5',
    name: 'Other airspace requirements',
    selectedValue: true,
    commentary: 'Comprehensive airspace assessment is conducted during pre-flight planning. NOTAMs are checked within 24 hours of each operation to identify any temporary restrictions or hazards. Continuous monitoring of temporary airspace restrictions is maintained throughout the operational period.',
    evidence: 'All applicable airspace requirements are identified during pre-flight planning. NOTAMs are checked within 24 hours of operation. Temporary airspace restrictions are monitored continuously.',
    citations: [mockCitations.sm5_airspace],
  },
  {
    id: 'arm-006',
    code: 'SM6',
    name: 'Pre-agreement of ANSP services',
    selectedValue: true,
    commentary: 'For operations conducted near controlled airspace, formal coordination with the Air Navigation Service Provider is established prior to flight commencement. Standard phraseology and communication procedures are documented and followed. This ensures ATC awareness and enables traffic deconfliction support.',
    evidence: 'For operations near controlled airspace, coordination with the Air Navigation Service Provider is established prior to flight. Standard phraseology and communication procedures are documented.',
    citations: [mockCitations.sm6_ansp],
  },
  {
    id: 'arm-007',
    code: 'SM7',
    name: 'Pre-agreement of UTM services',
    selectedValue: true,
    commentary: 'The operation is registered with the UTM service provider where such services are available. Real-time position data is shared through the UTM network enabling electronic conspicuity. Conflict alerts from the UTM system are actively monitored throughout each flight.',
    evidence: 'The operation is registered with the UTM service provider where available. Real-time position data is shared through the UTM network. Conflict alerts are monitored throughout the flight.',
    citations: [mockCitations.sm7_utm],
  },
  {
    id: 'arm-008',
    code: 'SM8',
    name: 'NOTAM of intended operation',
    selectedValue: true,
    commentary: 'A NOTAM is filed for operations that exceed 400ft AGL or are conducted within 5nm of an aerodrome. The NOTAM follows standard format requirements and includes accurate position coordinates, altitude information, and precise timing details to inform other airspace users.',
    evidence: 'A NOTAM is filed for operations exceeding 400ft AGL or within 5nm of an aerodrome. Standard NOTAM format is used with accurate position, altitude, and timing information.',
    citations: [mockCitations.sm8_notam],
  },
  {
    id: 'arm-009',
    code: 'SM9',
    name: 'Military low flying notification',
    selectedValue: true,
    commentary: 'Operations in areas with known military low flying activity are formally notified to the relevant military authority. The Low Flying Booking Cell is contacted for operations conducted in designated military low flying areas to ensure deconfliction with military training activities.',
    evidence: 'Operations in areas with known military low flying activity are notified to the relevant military authority. The Low Flying Booking Cell is contacted for operations in designated areas.',
    citations: [mockCitations.sm9_military],
  },
  {
    id: 'arm-010',
    code: 'SM10',
    name: 'Outreach to local flying clubs and pilots',
    selectedValue: true,
    commentary: 'Proactive outreach is conducted to local flying clubs and known pilots operating in the area. Information about planned operations is shared through direct contact and notice boards at nearby airfields. Contact details are exchanged to enable real-time coordination if required during operations.',
    evidence: 'Local flying clubs and known pilots in the area are informed of planned operations through direct contact and notice boards at nearby airfields. Contact details are exchanged for real-time coordination.',
    citations: [mockCitations.sm10_outreach],
  },
];

// ============================================================================
// Mock Extraction Results
// ============================================================================

const mockExtractionResults: ExtractionResults = {
  processId: 'proc-001',
  processedFileName: 'Concept of Operations - Survey Drone v2.1.pdf',
  processedFileMimeType: 'application/pdf',
  processedTime: '2024-03-15T14:30:00Z',
  importedTime: '2024-03-15T14:25:00Z',
  lastModifiedTime: '2024-03-15T14:30:00Z',
  lastModifiedBy: 'system',
  status: 'completed',
  entityScore: 0.87,
  minExtractedEntityScore: 0.6,
  schemaScore: 0.92,
  result: 'Successfully extracted SORA evidence from uploaded documents',
  confidence: 0.85,
  targetSchema: 'SORA_Evidence_v1.0',
  promptTokens: 4523,
  completionTokens: 2187,
  processOutput: 'Extraction completed successfully. 5 ground risk answers, 4 ground risk mitigations, 5 air risk answers, and 10 air risk mitigations extracted.',
  groundRiskAnswers: mockGroundRiskAnswers,
  groundRiskMitigations: mockGroundRiskMitigations,
  airRiskAnswers: mockAirRiskAnswers,
  airRiskMitigations: mockAirRiskMitigations,
};

// ============================================================================
// MockDataService Interface and Implementation
// ============================================================================

/**
 * Interface for the mock data service used in demo mode
 *
 * Requirements: 10.1, 10.2, 10.3
 */
export interface MockDataService {
  /** Get all mock documents */
  getDocuments(): Document[];
  /** Get mock extraction results */
  getExtractionResults(): ExtractionResults;
  /** Simulate document processing with progress updates */
  simulateProcessing(): Promise<ProcessingJob>;
  /** Get a specific document by ID */
  getDocumentById(id: string): Document | undefined;
  /** Simulate document upload */
  simulateUpload(file: File, name: string, classification: Document['classification']): Promise<UploadedDocument>;
  /** Get extraction results by process ID */
  getExtractionResultsById(processId: string): ExtractionResults | undefined;
  /** Delete a document */
  deleteDocument(id: string): void;
  /** Upload a new version of an existing document */
  uploadNewVersion(documentId: string, file: File): Promise<Document | undefined>;
  /** Store extraction results for later retrieval */
  storeExtractionResults(processId: string, results: ExtractionResults): void;

  // ============================================================================
  // ContentProcessorAPI Integration Methods (Requirements: 10.1, 10.2, 10.3)
  // ============================================================================

  /**
   * Get all available schemas
   * @returns Array of mock schemas
   * Requirement: 10.1, 10.3
   */
  getSchemas(): Schema[];

  /**
   * Get a specific schema by ID
   * @param schemaId - Schema ID to find
   * @returns Schema if found, undefined otherwise
   * Requirement: 10.1, 10.3
   */
  getSchemaById(schemaId: string): Schema | undefined;

  /**
   * Simulate document submission to ContentProcessorAPI
   * Returns a mock SubmitResponse with simulated delay
   * @param file - File to submit
   * @param schemaId - Schema ID to use for processing
   * @param metadataId - Metadata ID for the submission
   * @returns Promise resolving to SubmitResponse
   * Requirement: 10.1, 10.2
   */
  simulateSubmitDocument(
    file: File,
    schemaId: string,
    metadataId: string
  ): Promise<SubmitResponse>;

  /**
   * Simulate getting processing status from ContentProcessorAPI
   * Returns mock status with simulated delay
   * @param processId - Process ID to check status for
   * @returns Promise resolving to status object
   * Requirement: 10.1, 10.2
   */
  simulateGetProcessingStatus(
    processId: string
  ): Promise<{ status: ProcessingStatus; message: string; resourceUrl?: string }>;

  /**
   * Get all mock processed documents
   * @returns Array of mock ProcessedDocument objects
   * Requirement: 10.1, 10.3
   */
  getProcessedDocuments(): ProcessedDocument[];

  /**
   * Get a specific processed document by process ID
   * @param processId - Process ID to find
   * @returns ProcessedDocument if found, undefined otherwise
   * Requirement: 10.1, 10.3
   */
  getProcessedDocumentById(processId: string): ProcessedDocument | undefined;
}

/**
 * Implementation of MockDataService for demo mode
 *
 * Provides realistic SORA evidence data for all four review pages:
 * - Ground Risk Answers (5 fields)
 * - Ground Risk Mitigations (M1A, M1B, M1C, M2)
 * - Air Risk Answers (5 fields)
 * - Air Risk Mitigations (SM1-SM10)
 *
 * Includes varied confidence scores (low, medium, high) across mock data
 * to satisfy Requirement 12.5.
 *
 * Requirements: 12.2, 12.3, 12.4, 12.5
 */
class MockDataServiceImpl implements MockDataService {
  private documents: Document[] = [...mockDocuments];
  private extractions: Map<string, ExtractionResults> = new Map([
    ['proc-001', mockExtractionResults],
  ]);

  /**
   * Get all mock documents
   * @returns Array of mock documents
   */
  getDocuments(): Document[] {
    return this.documents;
  }

  /**
   * Get mock extraction results
   * @returns Mock extraction results with all SORA evidence data
   */
  getExtractionResults(): ExtractionResults {
    return mockExtractionResults;
  }

  /**
   * Simulate document processing with realistic progress updates
   * @returns Promise that resolves to a completed ProcessingJob
   */
  async simulateProcessing(): Promise<ProcessingJob> {
    const jobId = `job-${Date.now()}`;
    const startedAt = new Date().toISOString();

    // Simulate processing delay (2-3 seconds)
    await new Promise((resolve) => setTimeout(resolve, 2000 + Math.random() * 1000));

    const completedJob: ProcessingJob = {
      jobId,
      status: 'completed',
      progress: 100,
      startedAt,
      completedAt: new Date().toISOString(),
    };

    return completedJob;
  }

  /**
   * Get a specific document by ID
   * @param id - Document ID to find
   * @returns Document if found, undefined otherwise
   */
  getDocumentById(id: string): Document | undefined {
    return this.documents.find((doc) => doc.id === id);
  }

  /**
   * Simulate document upload in demo mode
   * Creates a mock uploaded document without actual storage interaction
   * @param file - File object to simulate uploading
   * @param name - Display name for the document
   * @param classification - Document classification type
   * @returns Promise resolving to mock UploadedDocument
   */
  async simulateUpload(
    file: File,
    name: string,
    classification: Document['classification']
  ): Promise<UploadedDocument> {
    // Simulate upload delay
    await new Promise((resolve) => setTimeout(resolve, 500 + Math.random() * 500));

    const newDocId = `doc-${Date.now()}`;
    const uploadedAt = new Date().toISOString();

    const uploadedDocument: UploadedDocument = {
      id: newDocId,
      name,
      classification,
      size: file.size,
      mimeType: file.type || 'application/pdf',
      uploadedAt,
      blobUrl: `https://storage.blob.core.windows.net/evidence-documents/${newDocId}/v1/${file.name}`,
    };

    // Add to internal documents list
    const newDocument: Document = {
      ...uploadedDocument,
      currentVersion: 1,
      versions: [
        {
          version: 1,
          blobUrl: uploadedDocument.blobUrl,
          size: file.size,
          uploadedAt,
          uploadedBy: 'demo-user',
        },
      ],
      createdAt: uploadedAt,
      updatedAt: uploadedAt,
      createdBy: 'demo-user',
    };

    this.documents.push(newDocument);

    return uploadedDocument;
  }

  /**
   * Get extraction results by process ID
   * @param processId - Process ID to find
   * @returns ExtractionResults if found, undefined otherwise
   */
  getExtractionResultsById(processId: string): ExtractionResults | undefined {
    return this.extractions.get(processId) || mockExtractionResults;
  }

  /**
   * Delete a document in demo mode
   * @param id - Document ID to delete
   */
  deleteDocument(id: string): void {
    const index = this.documents.findIndex((doc) => doc.id === id);
    if (index !== -1) {
      this.documents.splice(index, 1);
    }
  }

  /**
   * Upload a new version of an existing document
   * @param documentId - ID of the document to update
   * @param file - New file version
   * @returns Promise resolving to the updated Document
   */
  async uploadNewVersion(documentId: string, file: File): Promise<Document | undefined> {
    const document = this.documents.find((doc) => doc.id === documentId);
    if (!document) {
      return undefined;
    }

    // Simulate upload delay
    await new Promise((resolve) => setTimeout(resolve, 500 + Math.random() * 500));

    const newVersion = document.currentVersion + 1;
    const uploadedAt = new Date().toISOString();
    const newBlobUrl = `https://storage.blob.core.windows.net/evidence-documents/${documentId}/v${newVersion}/${file.name}`;

    const newVersionRecord = {
      version: newVersion,
      blobUrl: newBlobUrl,
      size: file.size,
      uploadedAt,
      uploadedBy: 'demo-user',
    };

    document.versions.push(newVersionRecord);
    document.currentVersion = newVersion;
    document.blobUrl = newBlobUrl;
    document.size = file.size;
    document.updatedAt = uploadedAt;

    return document;
  }

  /**
   * Store extraction results for later retrieval
   * @param processId - Process ID for the extraction
   * @param results - The extraction results to store
   */
  storeExtractionResults(processId: string, results: ExtractionResults): void {
    this.extractions.set(processId, results);
  }

  // ============================================================================
  // ContentProcessorAPI Integration Methods (Requirements: 10.1, 10.2, 10.3)
  // ============================================================================

  /**
   * Track processing status for simulated submissions
   * Maps processId to number of status checks (for simulating progress)
   */
  private processingStatusChecks: Map<string, number> = new Map();

  /**
   * Store for dynamically created processed documents
   */
  private processedDocuments: ProcessedDocument[] = [...mockProcessedDocuments];

  /**
   * Get all available schemas
   * @returns Array of mock schemas
   * Requirement: 10.1, 10.3
   */
  getSchemas(): Schema[] {
    return mockSchemas;
  }

  /**
   * Get a specific schema by ID
   * @param schemaId - Schema ID to find
   * @returns Schema if found, undefined otherwise
   * Requirement: 10.1, 10.3
   */
  getSchemaById(schemaId: string): Schema | undefined {
    return mockSchemas.find((schema) => schema.id === schemaId);
  }

  /**
   * Simulate document submission to ContentProcessorAPI
   * Returns a mock SubmitResponse with simulated delay
   *
   * Simulates realistic processing delays (500-1500ms) to mimic
   * actual API behavior in demo mode.
   *
   * @param file - File to submit
   * @param schemaId - Schema ID to use for processing
   * @param metadataId - Metadata ID for the submission
   * @returns Promise resolving to SubmitResponse
   * Requirement: 10.1, 10.2
   */
  async simulateSubmitDocument(
    file: File,
    schemaId: string,
    metadataId: string
  ): Promise<SubmitResponse> {
    // Simulate network delay (500-1500ms)
    await new Promise((resolve) => setTimeout(resolve, 500 + Math.random() * 1000));

    // Generate a new process ID
    const processId = `proc-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Initialize status tracking for this process
    this.processingStatusChecks.set(processId, 0);

    // Get the schema for this submission
    const schema = this.getSchemaById(schemaId);

    // Create a new ProcessedDocument in 'processing' state
    const newProcessedDocument: ProcessedDocument = {
      processId,
      processedFileName: file.name,
      processedFileMimeType: file.type || 'application/pdf',
      processedTime: null,
      importedTime: new Date().toISOString(),
      lastModifiedTime: new Date().toISOString(),
      lastModifiedBy: null,
      status: 'processing',
      entityScore: 0,
      minExtractedEntityScore: 0.6,
      schemaScore: 0,
      result: null,
      confidence: null,
      targetSchema: schema || null,
      promptTokens: 0,
      completionTokens: 0,
      processOutput: [
        {
          stepName: 'document_ingestion',
          processedTime: new Date().toISOString(),
          stepResult: { status: 'started', file_name: file.name },
        },
      ],
      extractedComparisonData: null,
      comment: null,
    };

    // Store the new processed document
    this.processedDocuments.push(newProcessedDocument);

    // Return the submit response
    const response: SubmitResponse = {
      message: `Document "${file.name}" submitted successfully for processing with schema "${schema?.className || schemaId}" and metadata "${metadataId}"`,
      status_url: `/contentprocessor/status/${processId}`,
    };

    return response;
  }

  /**
   * Simulate getting processing status from ContentProcessorAPI
   * Returns mock status with simulated delay
   *
   * Simulates realistic processing progression:
   * - First 2 checks: 'processing' status
   * - 3rd check onwards: 'completed' status
   *
   * This allows testing of polling behavior in demo mode.
   *
   * @param processId - Process ID to check status for
   * @returns Promise resolving to status object
   * Requirement: 10.1, 10.2
   */
  async simulateGetProcessingStatus(
    processId: string
  ): Promise<{ status: ProcessingStatus; message: string; resourceUrl?: string }> {
    // Simulate network delay (200-500ms)
    await new Promise((resolve) => setTimeout(resolve, 200 + Math.random() * 300));

    // Get current check count and increment
    const checkCount = this.processingStatusChecks.get(processId) || 0;
    this.processingStatusChecks.set(processId, checkCount + 1);

    // Find the processed document
    const processedDoc = this.processedDocuments.find((doc) => doc.processId === processId);

    // If not found, return failed status
    if (!processedDoc) {
      return {
        status: 'failed',
        message: `Process ${processId} not found`,
      };
    }

    // Simulate processing progression
    // After 2 status checks, mark as completed
    if (checkCount >= 2) {
      // Update the processed document to completed state
      processedDoc.status = 'completed';
      processedDoc.processedTime = new Date().toISOString();
      processedDoc.lastModifiedTime = new Date().toISOString();
      processedDoc.entityScore = 0.85 + Math.random() * 0.1;
      processedDoc.schemaScore = 0.90 + Math.random() * 0.08;
      processedDoc.result = {
        summary: 'Successfully extracted evidence from document',
        entities_found: Math.floor(10 + Math.random() * 20),
      };
      processedDoc.confidence = {
        overall: 0.80 + Math.random() * 0.15,
      };
      processedDoc.promptTokens = Math.floor(3000 + Math.random() * 2000);
      processedDoc.completionTokens = Math.floor(1500 + Math.random() * 1000);
      processedDoc.processOutput = [
        ...processedDoc.processOutput,
        {
          stepName: 'schema_matching',
          processedTime: new Date(Date.now() - 2000).toISOString(),
          stepResult: { schema_id: processedDoc.targetSchema?.id, match_score: processedDoc.schemaScore },
        },
        {
          stepName: 'entity_extraction',
          processedTime: new Date(Date.now() - 1000).toISOString(),
          stepResult: { entities_found: 15, confidence_avg: processedDoc.entityScore },
        },
        {
          stepName: 'validation',
          processedTime: new Date().toISOString(),
          stepResult: { validation_passed: true, warnings: [] },
        },
      ];
      processedDoc.extractedComparisonData = mockExtractionComparisonData;

      return {
        status: 'completed',
        message: 'Processing completed successfully',
        resourceUrl: `/contentprocessor/processed/${processId}`,
      };
    }

    // Still processing - update process output to show progress
    const progressStep = checkCount === 0 ? 'schema_matching' : 'entity_extraction';
    processedDoc.processOutput = [
      ...processedDoc.processOutput,
      {
        stepName: progressStep,
        processedTime: new Date().toISOString(),
        stepResult: { status: 'in_progress' },
      },
    ];

    return {
      status: 'processing',
      message: `Processing in progress (step ${checkCount + 1}/3)`,
    };
  }

  /**
   * Get all mock processed documents
   * @returns Array of mock ProcessedDocument objects
   * Requirement: 10.1, 10.3
   */
  getProcessedDocuments(): ProcessedDocument[] {
    return this.processedDocuments;
  }

  /**
   * Get a specific processed document by process ID
   * @param processId - Process ID to find
   * @returns ProcessedDocument if found, undefined otherwise
   * Requirement: 10.1, 10.3
   */
  getProcessedDocumentById(processId: string): ProcessedDocument | undefined {
    return this.processedDocuments.find((doc) => doc.processId === processId);
  }
}

// ============================================================================
// Singleton Instance and Factory
// ============================================================================

/**
 * Singleton instance of the mock data service
 */
let mockDataServiceInstance: MockDataServiceImpl | null = null;

/**
 * Get or create the mock data service instance
 * @returns MockDataService singleton instance
 */
export function getMockDataService(): MockDataService {
  if (!mockDataServiceInstance) {
    mockDataServiceInstance = new MockDataServiceImpl();
  }
  return mockDataServiceInstance;
}

/**
 * Reset the mock data service (useful for testing)
 */
export function resetMockDataService(): void {
  mockDataServiceInstance = null;
}

// ============================================================================
// Exported Mock Data (for direct access in tests)
// ============================================================================

export {
  mockDocuments,
  mockExtractionResults,
  mockGroundRiskAnswers,
  mockGroundRiskMitigations,
  mockAirRiskAnswers,
  mockAirRiskMitigations,
  mockCitations,
  // ContentProcessorAPI mock data exports
  mockSchemas,
  mockProcessedDocuments,
  mockProcessStepOutputs,
  mockExtractionComparisonData,
};

// Default export
export default getMockDataService;
